import { toast } from 'sonner';
import { registerActionHandler } from '../action-registry';
import { buildSmallAppUrl } from '../smallapp-url';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';

/**
 * `refreshSlides` — Refresh Data action for the Google Slides manifest.
 *
 * Mirrors `refreshDataSource`: POSTs the microservice's existing `/asset/refresh/:assetId`
 * route with the Firebase/share-link REST token (`getSmallAppRestToken`) — the SAME auth that
 * route already accepts (`verifyAccessTokenFirebase`). We use a client handler rather than an
 * `endpoint` action because the open-api proxy (`smallappProxyAction`) can't reach a path-param
 * route: it only special-cases `/asset/config` and otherwise POSTs the whole asset as the body,
 * and its RS256 service token 403s here.
 *
 * The route resolves the asset by id server-side and schedules the slides re-sync job
 * (`scheduleJobSlidesFresh(folder.id)`) — so the handler sends nothing but the id (in the path);
 * the `verifyAccessTokenFirebase` middleware injects the caller's `tokenData` for the ownership check.
 *
 * Disabled in add-mode (`assetId` only exists after the first save) — mirrors the manifest's
 * `disabled: 'add'` on the action.
 *
 * Manifest usage (on the read-only `webLink` field, step 2):
 *   { key: 'webLink', type: 'string', readOnly: true, action: {
 *       label: 'Refresh Data', handler: 'refreshSlides', disabled: 'add',
 *       onError: { message: 'Something went wrong! Please try again.' } } }
 */
registerActionHandler('refreshSlides', async ({ values, appMeta, apolloClient, t }) => {
  const apiUrl = appMeta?.apiUrl;
  const assetId = values.assetId;
  // Defensive only — the action is `disabled: 'add'` in the manifest, so it can't fire until the
  // asset exists (assetId present). Return silently rather than surfacing an unreachable message.
  if (!apiUrl || !assetId) return;

  const url = buildSmallAppUrl(apiUrl, `asset/refresh/${String(assetId)}`);
  // Firebase/share-link-aware REST token the slides microservice accepts (the open-api proxy's
  // RS256 service token 403s) — same token method as `useFetchOptions` / `checkPublic`.
  const token = await getSmallAppRestToken(apolloClient);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    // Id travels in the path; `verifyAccessTokenFirebase` injects `tokenData` for the ownership
    // check and the route resolves `folder.id` from the stored asset — nothing to send here.
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    toast.error(t('Failed to refresh data'));
    return;
  }

  toast.success(t('Data refreshed'));
});
